import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/preschool`;
const MAX_RETRIES = 4;
const CONC = 4;

const Ctx = createContext(null);

async function retryingPut(url, blob, contentType, signal) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    try {
      const res = await fetch(url, {
        method: "PUT", body: blob, signal,
        headers: contentType ? { "Content-Type": contentType } : {},
      });
      if (res.ok) return;
      lastErr = new Error(`R2 PUT ${res.status}`);
      if (res.status < 500 && res.status !== 408 && res.status !== 429) break;
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      lastErr = err;
    }
    await new Promise((r) => setTimeout(r, 800 * 2 ** attempt));
  }
  throw lastErr || new Error("PUT failed");
}

const localDay = (ms) => {
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const meta = (f, fallbackType) => ({
  filename: f.name,
  content_type: f.type || fallbackType,
  rel_path: f.webkitRelativePath || f.name,
  file_day: localDay(f.lastModified),
  file_ts: f.lastModified || null,
});

export function PreschoolUploadProvider({ children }) {
  const [uploads, setUploads] = useState({});
  const uploadsRef = useRef({});
  const cancelledRef = useRef(new Set());
  const abortersRef = useRef(new Map());
  const chainRef = useRef(Promise.resolve());

  useEffect(() => {
    const onUnload = (e) => {
      if (Object.values(uploadsRef.current).some((u) => u.status === "uploading")) {
        e.preventDefault();
        e.returnValue = "Uploads are still in progress.";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, []);

  const patch = (id, p) => setUploads((prev) => {
    const next = { ...prev, [id]: { ...(prev[id] || {}), ...p } };
    uploadsRef.current = next;
    return next;
  });
  const remove = (id) => setUploads((prev) => {
    const next = { ...prev };
    delete next[id];
    uploadsRef.current = next;
    return next;
  });

  const cancelUpload = (id) => {
    cancelledRef.current.add(id);
    abortersRef.current.get(id)?.abort();
    abortersRef.current.delete(id);
    remove(id);
  };

  const runUpload = async ({ name, sheetFile, rosterFile, photoFiles }) => {
    let init;
    try {
      ({ data: init } = await axios.post(`${API}/tasks/init`, {
        name,
        sheet: meta(sheetFile, "application/pdf"),
        roster: meta(rosterFile, "text/csv"),
        photos: photoFiles.map((f) => meta(f, "application/octet-stream")),
      }));
    } catch (err) {
      toast.error(`Couldn't start task: ${err.response?.data?.detail || err.message}`);
      return;
    }
    const id = init.task_id;
    const jobs = [
      { p: init.upload_urls.sheet, file: sheetFile },
      { p: init.upload_urls.roster, file: rosterFile },
      ...init.upload_urls.photos.map((p, i) => ({ p, file: photoFiles[i] })),
    ];
    const total = jobs.length;
    patch(id, { name: init.name, status: "uploading", done: 0, failed: 0, total });
    const ctl = new AbortController();
    abortersRef.current.set(id, ctl);
    let cursor = 0, done = 0, failed = 0, lastPing = 0;
    let pendingIdx = [];
    let trailing = null;
    const ping = async () => {
      clearTimeout(trailing);
      trailing = null;
      lastPing = Date.now();
      const batch = pendingIdx;
      pendingIdx = [];
      try {
        await axios.post(`${API}/tasks/${id}/upload-progress`, { done: done - 2, failed, uploaded: batch });
      } catch {
        pendingIdx = batch.concat(pendingIdx);
      }
    };

    // Sheet + roster first so the OCR can start the moment uploads finish.
    const putOne = async ({ p, file }, photoIdx = null) => {
      try {
        await retryingPut(p.upload_url, file, p.content_type, ctl.signal);
        done++;
      } catch (err) {
        if (err?.name === "AbortError" || photoIdx === null) throw err;
        failed++;
        return;
      }
      if (cancelledRef.current.has(id) || photoIdx === null) return;
      pendingIdx.push(photoIdx);
      patch(id, { done, failed });
      if (Date.now() - lastPing > 2000) ping();
      else if (!trailing) trailing = setTimeout(ping, 2000);
    };
    try {
      // Phase 1: sheet + names → OCR starts while the photos keep uploading.
      await putOne(jobs[0]);
      await putOne(jobs[1]);
      patch(id, { done, failed });
      await axios.post(`${API}/tasks/${id}/sheet-ready`).catch(() => {});
      toast.success("Sheet uploaded — reading numbers while photos upload");
      cursor = 2;
      await Promise.all(new Array(Math.min(CONC, jobs.length - 2)).fill(0).map(async () => {
        while (cursor < jobs.length && !cancelledRef.current.has(id)) {
          const k = cursor++;
          await putOne(jobs[k], k - 2);
        }
      }));
    } catch (err) {
      if (err?.name !== "AbortError") {
        patch(id, { status: "failed", error: "Sheet or names file failed to upload" });
        toast.error("Sheet or names file failed to upload — delete the task and try again");
      }
      return;
    } finally {
      clearTimeout(trailing);
      abortersRef.current.delete(id);
    }
    if (cancelledRef.current.has(id)) return;
    try {
      await ping();
      if (pendingIdx.length) await ping();
      await axios.post(`${API}/tasks/${id}/finalize`);
      toast.success(failed ? `Photos uploaded with ${failed} failure(s)` : "All photos uploaded");
      patch(id, { status: "done" });
      setTimeout(() => remove(id), 3000);
    } catch (err) {
      if (err.response?.status === 404) remove(id);
      else patch(id, { status: "failed", error: err.response?.data?.detail || err.message });
    }
  };

  const enqueueTask = (params) => {
    const pid = `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const total = params.photoFiles.length + 2;
    patch(pid, { name: params.name, status: "waiting", done: 0, failed: 0, total, placeholder: true });
    chainRef.current = chainRef.current.catch(() => {}).then(async () => {
      if (cancelledRef.current.has(pid)) return remove(pid);
      remove(pid);
      await runUpload(params);
    });
    return pid;
  };

  return (
    <Ctx.Provider value={{ uploads, enqueueTask, cancelUpload }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePreschoolUploads() {
  const c = useContext(Ctx);
  if (!c) throw new Error("usePreschoolUploads outside provider");
  return c;
}
